/* eslint-disable react-refresh/only-export-components */

import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowRight } from 'lucide-react';
import type { OutgoingTransactionDetailLine } from '@/api/transactions/types';
import { useOutgoingTransactionOverview } from '@/app/(client)/transactions/-hooks/useOutgoingTransactionOverview';
import { ConnectedPartiesCard } from '@/components/shared/ConnectedPartiesCard';
import { PartyRow } from '@/components/shared/ConnectedPartiesCard/PartyRow';
import { DataTable } from '@/components/shared/DataTable';
import { DocumentRulesTable } from '@/components/shared/DocumentRulesTable';
import { FieldCard, FieldRow } from '@/components/shared/FieldLayout';
import { useTranslator } from '@/hooks/useTranslator';
import { SHIPMENT_METHOD_LABELS } from '@/lib/constants/fiberTransactions';
import { formatDateDisplay } from '@/lib/utils/formatters';

export const Route = createFileRoute(
  '/(client)/transactions/(pages)/(detail)/outgoing/$txId/overview',
)({ component: OutgoingTransactionOverviewTab });

function OutgoingTransactionOverviewTab() {
  const { txId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'fiberTransactions' });
  const {
    tx,
    linkedIncoming,
    linkedLines,
    effectiveRules,
    documents,
    handleFileSelected,
    handleFileUploaded,
    handleRemoveFile,
  } = useOutgoingTransactionOverview(txId);

  if (!tx) return null;

  const methodLabel =
    SHIPMENT_METHOD_LABELS[tx.transportationMethod ?? ''] ??
    tx.transportationMethod ??
    '—';

  const lineColumns: ColumnDef<OutgoingTransactionDetailLine>[] = [
    {
      id: 'product',
      header: t('detail.product'),
      cell: ({ row }) => (
        <span className='text-xs font-medium'>
          {row.original.product?.name ?? row.original.productName ?? '—'}
        </span>
      ),
    },
    {
      id: 'code',
      header: t('detail.code'),
      cell: ({ row }) => (
        <span className='text-xs'>
          {row.original.product?.code ?? row.original.productCode ?? '—'}
        </span>
      ),
    },
    {
      id: 'level',
      header: t('detail.level'),
      cell: ({ row }) => (
        <span className='text-xs text-muted-foreground'>
          {row.original.product?.productLevelName ?? '—'}
        </span>
      ),
    },
    {
      id: 'type',
      header: t('detail.type'),
      cell: ({ row }) => (
        <span className='text-xs text-muted-foreground'>
          {row.original.product?.productCategoryName ??
            row.original.productCategoryName ??
            '—'}
        </span>
      ),
    },
    {
      id: 'materials',
      header: t('detail.materials'),
      cell: ({ row }) => {
        const mats = row.original.product?.materials ?? [];
        if (!mats.length)
          return <span className='text-xs text-muted-foreground'>—</span>;
        return (
          <span className='text-xs text-muted-foreground'>
            {mats.map(m => `${m.percentage}% ${m.name}`).join(', ')}
          </span>
        );
      },
    },
    {
      id: 'lots',
      header: t('detail.lotNo'),
      cell: ({ row }) => {
        const lots = row.original.product?.lots ?? row.original.lots ?? [];
        if (!lots.length)
          return <span className='text-xs text-muted-foreground'>—</span>;
        return (
          <span className='text-xs text-muted-foreground'>
            {lots
              .map(
                l =>
                  `${l.fiberLotName ?? l.name ?? ''} - ${l.kgQuantity ?? l.quantity ?? 0}kg`,
              )
              .join(', ')}
          </span>
        );
      },
    },
    {
      id: 'secondaryQty',
      header: t('detail.secondaryQty'),
      cell: ({ row }) => {
        const qty = row.original.totalKgQuantity;
        return (
          <span className='text-xs font-medium'>
            {qty != null ? `${qty} kg` : '—'}
          </span>
        );
      },
    },
  ];

  return (
    <div className='flex flex-col gap-6'>
      {/* Transaction info (left) + Shipper/Receiver (right) */}
      <div className='grid grid-cols-1 @md:grid-cols-2 gap-4 items-stretch'>
        {/* Left: transaction details */}
        <FieldCard title={t('detail.transactionInfo')}>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.transactionNumber')}
              </span>
            }
          >
            <span className='text-xs font-medium'>
              {tx.transactionNumber ?? '—'}
            </span>
          </FieldRow>
          {typeof linkedIncoming?.transactionNumber === 'string' && (
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.linkedIncoming')}
                </span>
              }
            >
              <span className='text-xs'>
                {linkedIncoming.transactionNumber}
              </span>
            </FieldRow>
          )}
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.status')}
              </span>
            }
          >
            <span className='text-xs'>{tx.status ?? '—'}</span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.transportMethod')}
              </span>
            }
          >
            <span className='text-xs'>{methodLabel}</span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.transportId')}
              </span>
            }
          >
            <span className='text-xs'>
              {tx.transportationIdentification ?? '—'}
            </span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.date')}
              </span>
            }
          >
            <span className='text-xs'>
              {tx.transactionDate ? formatDateDisplay(tx.transactionDate) : '—'}
            </span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.createdBy')}
              </span>
            }
          >
            <span className='text-xs'>{tx.createdByUserName ?? '—'}</span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.createdAt')}
              </span>
            }
          >
            <span className='text-xs'>
              {tx.createdAt ? formatDateDisplay(tx.createdAt) : '—'}
            </span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.updatedBy')}
              </span>
            }
          >
            <span className='text-xs'>{tx.updatedByUserName ?? '—'}</span>
          </FieldRow>
          <FieldRow
            label={
              <span className='text-xs text-muted-foreground'>
                {t('detail.updatedAt')}
              </span>
            }
          >
            <span className='text-xs'>
              {tx.updatedAt ? formatDateDisplay(tx.updatedAt) : '—'}
            </span>
          </FieldRow>
        </FieldCard>

        {/* Right: shipper + receiver in one connected card */}
        <ConnectedPartiesCard
          dividerLabel={t('detail.transactionFlow')}
          dividerIcon={<ArrowRight className='size-3' />}
          topSlot={
            <PartyRow
              label={t('detail.shipper')}
              companyId={tx.company.id}
              name={tx.company.name}
              imageUrl={tx.company.featuredImageFileUrl}
              country={tx.company.country}
              facilityProcess={tx.facilityProcess}
              viewLabel={t('detail.view')}
            />
          }
          bottomSlot={
            <PartyRow
              label={t('detail.receiver')}
              companyId={tx.withCompany.id}
              name={tx.withCompany.name}
              imageUrl={tx.withCompany.featuredImageFileUrl}
              country={tx.withCompany.country}
              facilityProcess={tx.withCompanyFacilityProcess}
              viewLabel={t('detail.view')}
            />
          }
        />
      </div>

      {/* Outgoing shipment lines — DataTable */}
      <div className='flex flex-col gap-2'>
        <p className='text-sm font-semibold'>{t('detail.outgoingLines')}</p>
        <div className='overflow-x-auto'>
          <DataTable
            data={tx.lines ?? []}
            columns={lineColumns}
            rowIdKey='id'
            disablePagination
            hideFooter
            emptyMessage={t('detail.noLines')}
          />
        </div>
      </div>

      {/* Received shipment lines (linked incoming transaction) */}
      {tx.handshakedFiberTransactionId && (
        <div className='flex flex-col gap-2'>
          <p className='text-sm font-semibold'>{t('detail.linkedIncoming')}</p>
          <div className='overflow-x-auto'>
            <DataTable
              data={linkedLines}
              columns={lineColumns}
              rowIdKey='id'
              disablePagination
              hideFooter
              emptyMessage={t('detail.noLines')}
            />
          </div>
        </div>
      )}

      {/* Required documentations */}
      {effectiveRules.length > 0 && (
        <div className='flex flex-col gap-2'>
          <p className='text-sm font-semibold'>{t('detail.requiredDocs')}</p>
          <DocumentRulesTable
            rules={effectiveRules}
            documents={documents}
            simpleColumns
            onFileSelected={handleFileSelected}
            onFileUploaded={handleFileUploaded}
            onRemoveFile={handleRemoveFile}
          />
        </div>
      )}
    </div>
  );
}
